import { useCallback, useRef, useState } from "react";

// Reads the public Turnstile site key once. When it is unset (local dev), CAPTCHA is treated as disabled and
// the gate auto-passes — this mirrors the backend's CaptchaOptions.Enabled=false dev path (a disabled verifier
// returns true even for an empty token). DO NOT ship without a site key in production.
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

export const isCaptchaEnabled = () => !!SITE_KEY;

/**
 * Owns the CAPTCHA token lifecycle so callers never touch the widget, its ref, expiry, or the
 * `X-Captcha-Token` header. Spread `bind` onto <Captcha/>, gate your submit on `isVerified`, hand the opaque
 * `token` to the PageFunction (which sets the header), and call `reset()` after submit or a server rejection.
 *
 *   const captcha = useCaptcha();
 *   <Captcha {...captcha.bind} />
 *   <button disabled={!captcha.isVerified}>Submit</button>
 *   await startApplication({ request, captchaToken: captcha.token, ...statusCbs });
 *   captcha.reset();
 */
export const useCaptcha = () => {
  const enabled = isCaptchaEnabled();
  const widgetRef = useRef(null);
  const [token, setToken] = useState("");
  // Disabled (dev) => already "verified" with an empty token; enabled (prod) => not until solved.
  const [isVerified, setIsVerified] = useState(!enabled);

  const handleVerify = useCallback((value) => {
    setToken(value || "");
    setIsVerified(!!value);
  }, []);

  const handleExpire = useCallback(() => {
    setToken("");
    setIsVerified(false);
  }, []);

  const reset = useCallback(() => {
    if (widgetRef.current && typeof widgetRef.current.reset === "function") {
      widgetRef.current.reset();
    }
    setToken("");
    setIsVerified(!enabled);
  }, [enabled]);

  return {
    token,
    isVerified,
    reset,
    bind: { widgetRef, onVerify: handleVerify, onExpire: handleExpire },
  };
};

export default useCaptcha;
